import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { api } from "../../common/setupApi";
import { getAvatarsPlayers } from "../photos/photosSlice";
import { selectUser, selectUserGroup } from "../user/userSlice";

export const getPlayers = createAsyncThunk(
  "squad/get",
  async (_, { dispatch, getState, rejectWithValue }) => {
    const { isPlayer } = selectUserGroup(getState());
    return api
      .get("players/")
      .then((res) => {
        if (isPlayer) {
          dispatch(
            squadActions.setSelectedPlayer(
              res.data.find((d) => d.pk === selectUser(getState()).data.pk)
            )
          );
        }

        dispatch(getAvatarsPlayers(res.data));

        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectPlayers(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

export const getSquads = createAsyncThunk(
  "squad/getSquads",
  async (_, { rejectWithValue }) => {
    return api
      .get("teams/")
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (_, { getState }) => {
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectSquads(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

const initialState = {
  search: "",
  players: {
    selected: {},
    status: "idle",
    data: [],
    error: null,
  },
  squads: {
    status: "idle",
    error: null,
    data: [],
  },
};

const squadSlice = createSlice({
  name: "squad",
  initialState,
  reducers: {
    addSquad: (state, action) => {
      const isDefault = action.payload.default;
      const added = [...state.squads.data, action.payload];
      if (isDefault) {
        state.squads.data = added.map((d) =>
          d.id === action.payload.id ? d : { ...d, default: false }
        );
      } else {
        state.squads.data = added;
      }
    },
    setSquad: (state, action) => {
      const isDefault = action.payload.default;
      if (isDefault) {
        state.squads.data = state.squads.data.map((d) =>
          d.id === action.payload.id ? action.payload : { ...d, default: false }
        );
      } else {
        state.squads.data = state.squads.data.map((d) =>
          d.id === action.payload.id ? action.payload : d
        );
      }
    },
    setWellnessActivation: (state, action) => {
      state.squads.data = state.squads.data.map((d) =>
        d.activated ? { ...d, wellness_active: action.payload } : d
      );
    },
    deleteSquad: (state, action) => {
      state.squads.data = state.squads.data.filter(
        (d) => d.id !== action.payload
      );
    },
    setSelectedPlayer: (state, action) => {
      state.players.selected = action.payload;
    },
    setWellnessCompletion: (state) => {
      state.players.selected.wellness_datetime = String(new Date());
      state.players.selected.wellness_answered = true;
      state.players.data = state.players.data.map((d) =>
        d.pk === state.players.selected.pk
          ? {
              ...d,
              wellness_datetime: String(new Date()),
              wellness_answered: true,
            }
          : d
      );
    },
    setPlayer: (state, action) => {
      state.players.data = state.players.data.map((d) =>
        d.pk === action.payload.pk ? action.payload : d
      );
    },
    deletePlayer: (state, action) => {
      state.players.data = state.players.data.filter(
        (d) => d.pk !== action.payload
      );
    },
    setSearch: (state, action) => {
      state.search = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPlayers.pending, (state) => {
        state.players.status = "pending";
        state.players.error = null;
      })
      .addCase(getPlayers.fulfilled, (state, action) => {
        state.players.status = "fulfilled";
        state.players.data = action.payload;
      })
      .addCase(getPlayers.rejected, (state, action) => {
        state.players.status = "rejected";
        state.players.error = action.payload;
      })
      .addCase(getSquads.pending, (state) => {
        state.squads.status = "pending";
        state.squads.error = null;
      })
      .addCase(getSquads.fulfilled, (state, action) => {
        state.squads.status = "fulfilled";
        state.squads.data = action.payload;
      })
      .addCase(getSquads.rejected, (state, action) => {
        state.squads.status = "rejected";
        state.squads.error = action.payload;
      })
      .addCase("auth/logout", () => initialState);
  },
});

export const squadActions = squadSlice.actions;

const _selectPlayers = (state) => state.squad.players;

export const selectPlayers = createSelector(
  [(state) => _selectPlayers(state)],
  (players) => {
    const data = players.data;
    const status = players.status;
    const error = players.error;
    const isLoading = status === "pending";
    return { data, status, error, isLoading };
  }
);

export const selectSelectedPlayer = createSelector(
  [(state) => state.squad.players.selected],
  (player) => player || {}
);

const _selectSquads = (state) => state.squad.squads;

export const selectSquads = createSelector(
  [(state) => _selectSquads(state)],
  (squads) => {
    const data = squads.data;
    const status = squads.status;
    const error = squads.error;
    const isLoading = status === "pending";
    return { data, status, error, isLoading };
  }
);

// memo ok
export const selectActiveSquad = createSelector(
  [(state) => selectSquads(state).data],
  (squads) => squads.find((d) => d.activated)?.name || ""
);

export const selectActiveSquadId = createSelector(
  [(state) => selectSquads(state).data],
  (squads) => squads.find((d) => d.activated)?.id
);

export const selectPlayersSearchValue = createSelector(
  (state) => state.squad.search,
  (search) => search || ""
);

const normalize = (str) => {
  if (!str) return "";
  try {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "") // enlève accents
      .replace(/[_/()—-]/g, " ") // normalise séparateurs
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    return str;
  }
};

export const selectPlayersFiltered = createSelector(
  [
    (state) => selectPlayers(state).data,
    (state) => selectPlayersSearchValue(state),
  ],
  (items, filter) => {
    if (!filter) return items;

    try {
      const q = normalize(filter);

      return items.filter((item) => {
        const display_name = normalize(item.display_name || "");
        const first_name = normalize(item.first_name);
        const last_name = normalize(item.last_name);
        const position = normalize(item.position || "");
        const username = normalize(item.username);
        const cardio_id = normalize(item.cardio_id || "");
        const gps_id = normalize(item.gps_id || "");
        const number = normalize(item.number?.toString() || "");

        return (
          display_name.includes(q) ||
          first_name.includes(q) ||
          last_name.includes(q) ||
          position.includes(q) ||
          username.includes(q) ||
          cardio_id.includes(q) ||
          gps_id.includes(q) ||
          number.includes(q)
        );
      });
    } catch (error) {
      console.error("Error in search players: ", error);
      return items;
    }
  }
);

export default squadSlice.reducer;
